import { useState } from 'react';
import { SAUser } from '../../../hooks/useSuperAdmin';

const SA_ACCENT = '#6366F1';

const ROLE_STYLES: Record<string, string> = {
  super_admin: 'bg-indigo-100 text-indigo-700',
  owner: 'bg-amber-100 text-amber-700',
  admin: 'bg-amber-100 text-amber-700',
  agent: 'bg-teal-100 text-teal-700',
  finance: 'bg-sky-100 text-sky-700',
  viewer: 'bg-slate-100 text-slate-500',
};

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  owner: 'Agency Owner',
  admin: 'Admin',
  agent: 'Agent',
  finance: 'Finance',
  viewer: 'Viewer',
};

interface SAUsersProps {
  users: SAUser[];
  loading: boolean;
}

function formatLoginDate(iso: string | null) {
  if (!iso) return 'Never';
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000 / 60);
  if (diff < 1) return 'Just now';
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  if (diff < 10080) return `${Math.floor(diff / 1440)}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function initials(name: string) {
  return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

export default function SAUsers({ users, loading }: SAUsersProps) {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [agencyFilter, setAgencyFilter] = useState('All');
  const [inviteModal, setInviteModal] = useState(false);

  const agencyNames = Array.from(new Set(users.map((u) => u.tenant_name))).sort();

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      u.full_name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.tenant_name.toLowerCase().includes(q);
    const matchRole = roleFilter === 'All' || u.role === roleFilter;
    const matchAgency = agencyFilter === 'All' || u.tenant_name === agencyFilter;
    return matchSearch && matchRole && matchAgency;
  });

  const roleStats = [
    { key: 'super_admin', label: 'Super Admin', count: users.filter((u) => u.role === 'super_admin').length },
    { key: 'owner', label: 'Owners', count: users.filter((u) => u.role === 'owner' || u.role === 'admin').length },
    { key: 'agent', label: 'Agents', count: users.filter((u) => u.role === 'agent').length },
    { key: 'finance', label: 'Finance', count: users.filter((u) => u.role === 'finance').length },
  ];

  return (
    <div className="space-y-5">
      {/* Role stats */}
      <div className="grid grid-cols-4 gap-4">
        {roleStats.map((r) => (
          <div
            key={r.key}
            className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3 cursor-pointer hover:border-indigo-200 transition-colors"
            onClick={() => setRoleFilter(r.key === roleFilter ? 'All' : r.key)}
          >
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${ROLE_STYLES[r.key]}`}>
              {r.label}
            </span>
            <span className="text-2xl font-bold text-slate-800">{r.count}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-4 flex-wrap">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="pl-9 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-sm text-slate-600 placeholder-slate-400 focus:outline-none focus:border-slate-300 w-64"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {['All', 'super_admin', 'owner', 'agent', 'finance', 'viewer'].map((r) => (
            <button
              key={r}
              onClick={() => setRoleFilter(r)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                roleFilter === r ? 'text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
              }`}
              style={roleFilter === r ? { backgroundColor: SA_ACCENT } : {}}
            >
              {r === 'All' ? 'All Roles' : ROLE_LABELS[r] || r}
            </button>
          ))}
        </div>
        <select
          value={agencyFilter}
          onChange={(e) => setAgencyFilter(e.target.value)}
          className="ml-auto px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-sm text-slate-600 focus:outline-none cursor-pointer"
        >
          <option value="All">All Agencies</option>
          {agencyNames.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <button
          onClick={() => setInviteModal(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
          style={{ backgroundColor: SA_ACCENT }}
        >
          <i className="ri-user-add-line" />
          Invite Admin
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-50">
          <p className="text-sm font-bold text-slate-800">
            Platform Users{' '}
            <span className="text-slate-400 font-normal">({filtered.length} shown)</span>
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <i className="ri-group-line text-4xl text-slate-200 mb-3" />
            <p className="text-sm font-semibold text-slate-400">No users found</p>
            <p className="text-xs text-slate-300 mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-400 uppercase tracking-wider">
                <th className="text-left px-6 py-3 font-medium">User</th>
                <th className="text-left px-4 py-3 font-medium">Agency</th>
                <th className="text-left px-4 py-3 font-medium">Role</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Last Login</th>
                <th className="text-left px-4 py-3 font-medium">Joined</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-6 py-3.5">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: SA_ACCENT }}
                      >
                        {initials(u.full_name)}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{u.full_name}</p>
                        <p className="text-xs text-slate-400">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-slate-600">{u.tenant_name}</td>
                  <td className="px-4 py-3.5">
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
                        ROLE_STYLES[u.role] || 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                        u.is_active
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-xs text-slate-400">
                    {formatLoginDate(u.last_login_at)}
                  </td>
                  <td className="px-4 py-3.5 text-xs text-slate-400">
                    {new Date(u.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-6 py-3.5">
                    <div className="flex items-center gap-1 justify-end">
                      <button className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                        <i className="ri-eye-line text-sm" />
                      </button>
                      <button className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                        <i className="ri-forbid-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Invite Modal */}
      {inviteModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-slate-800">Invite Platform Admin</h3>
              <button
                onClick={() => setInviteModal(false)}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Full Name</label>
                <input
                  type="text"
                  placeholder="Admin name"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Email Address</label>
                <input
                  type="email"
                  placeholder="admin@travelcrm.com"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Access Level</label>
                <select className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none cursor-pointer">
                  <option value="super_admin">Super Admin (full access)</option>
                  <option value="support">Support (read + moderate)</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setInviteModal(false)}
                  className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setInviteModal(false)}
                  className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer"
                  style={{ backgroundColor: SA_ACCENT }}
                >
                  Send Invite
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
